import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Rocket, CalendarDays, Check, ArrowDown, Sofa, Package, Smartphone,
  Building2, Plug, ScanSearch, Focus, Image as ImageIcon, Layers, Ruler,
  Sun, Contrast, Monitor, Users, UserCog, ShieldCheck, ScrollText, Lock,
  Code2, Infinity as InfinityIcon, Cloud,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { CALENDLY_DEMO_URL } from "@/constants/links";

import Navbar from "@/components/home/Navbar";
import Footer from "@/components/home/Footer";
import FinalCTA from "@/components/home/FinalCTA";
import HeroIntro from "@/components/home/HeroIntro";

import platformPreviewVideo from "@/assets/providedassets/productfrontvedio.mp4";
import vizImg from "@/assets/providedassets/ai visuzalization.jpeg";
import catalogImg from "@/assets/providedassets/PRODUCT CATALOGE MANAGEMENT.jpg.jpeg";
import hubImg from "@/assets/providedassets/White Label Platform.jpeg";
import whiteLabelImg from "@/assets/providedassets/Customer Experience Hub.jpeg";
import devImg from "@/assets/providedassets/developer.jpeg";

// Category marquee imagery — each image matched to its label.
import catFurniture from "@/assets/providedassets/builtfurniture.jpg";
import catFlooring from "@/assets/providedassets/flooringproduct.jpg";
import catPaint from "@/assets/providedassets/builtproductpaint.jpg";
import catRugs from "@/assets/providedassets/rugs1.jpg";
import catLighting from "@/assets/pendant-light.jpg";
import catTiles from "@/assets/providedassets/tile2.jpg";
import catDecor from "@/assets/providedassets/builthomedecor.jpg";
import catCurtains from "@/assets/providedassets/curtains2.jpg";
import catOutdoor from "@/assets/providedassets/homedecorproduct.jpg";
import catCommercial from "@/assets/providedassets/wall1.jpg";
import catKitchens from "@/assets/providedassets/builtrealstates.jpg";
import catBathrooms from "@/assets/providedassets/builtproductwallpaper.jpg";

const MARQUEE_ROW_1 = [
  { img: catFurniture, label: "Furniture" },
  { img: catFlooring, label: "Flooring" },
  { img: catPaint, label: "Paint" },
  { img: catRugs, label: "Rugs" },
  { img: catLighting, label: "Lighting" },
  { img: catTiles, label: "Tiles" },
];
const MARQUEE_ROW_2 = [
  { img: catDecor, label: "Home Décor" },
  { img: catCurtains, label: "Curtains" },
  { img: catOutdoor, label: "Outdoor Products" },
  { img: catCommercial, label: "Commercial Interiors" },
  { img: catKitchens, label: "Kitchens" },
  { img: catBathrooms, label: "Bathrooms" },
];

function MarqueeRow({ images, duration, reverse }) {
  const track = [...images, ...images];
  return (
    <motion.div
      className="flex flex-nowrap gap-4"
      animate={{ x: reverse ? ["-50%", "0%"] : ["0%", "-50%"] }}
      transition={{ duration, repeat: Infinity, ease: "linear" }}
    >
      {track.map((item, i) => (
        <div key={i} className="relative h-28 w-40 flex-shrink-0 rounded-2xl overflow-hidden md:h-36 md:w-56">
          <img src={item.img} alt={item.label} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/40 rounded-2xl" />
          <span className="absolute inset-0 flex items-center justify-center text-white text-sm font-semibold text-center px-2 md:text-base">
            {item.label}
          </span>
        </div>
      ))}
    </motion.div>
  );
}

const CORE_PRODUCTS = [
  {
    icon: Sofa,
    img: vizImg,
    name: "AI Room Visualization",
    tagline: "Let customers preview products before they buy.",
    body: "Customers upload a photo of their room, select products from your catalog, and receive a realistic AI-generated visualization in seconds.",
    listTitle: "Key Capabilities",
    items: [
      "Replace existing products", "Add new furniture", "Change wall colors",
      "Update flooring", "Swap décor", "Generate photorealistic results",
      "Compare multiple design options", "High-resolution downloads",
    ],
  },
  {
    icon: Package,
    img: catalogImg,
    name: "Product Catalog Management",
    tagline: "Organize your products once. Visualize them everywhere.",
    body: "Manage your complete catalog from a centralized dashboard.",
    listTitle: "Features",
    items: [
      "Bulk product uploads", "Product categories", "Brand collections",
      "Tags & filters", "Product metadata", "Product availability",
      "Version control", "Cloud storage",
    ],
  },
  {
    icon: Smartphone,
    img: hubImg,
    name: "Customer Experience Hub",
    tagline: "Create interactive shopping experiences.",
    body: "Customers can access NEIV from your website, store, QR codes, or shared links.",
    listTitle: "Features",
    items: [
      "QR Code Experiences", "Shareable Links", "Website Embeds",
      "Mobile Responsive", "In-store Kiosks", "Sales Team Access",
    ],
  },
  {
    icon: Building2,
    img: whiteLabelImg,
    name: "White Label Platform",
    tagline: "Launch under your own brand.",
    body: "Offer AI visualization using your own company identity.",
    listTitle: "Includes",
    items: [
      "Custom Domain", "Brand Logo", "Custom Colors",
      "Personalized Emails", "Customer Portal",
    ],
  },
  {
    icon: Plug,
    img: devImg,
    name: "Developer Platform",
    tagline: "Integrate NEIV into your existing systems.",
    body: "Connect NEIV with the tools your business already runs on.",
    listTitle: "Available integrations",
    items: [
      "REST API", "Webhooks", "Shopify", "WooCommerce",
      "Magento", "Custom ERP", "CRM Platforms",
    ],
  },
];

const AI_CAPABILITIES = [
  { icon: ScanSearch, label: "Scene Recognition" },
  { icon: Focus, label: "Object Detection" },
  { icon: ImageIcon, label: "Background Preservation" },
  { icon: Layers, label: "Material Understanding" },
  { icon: Ruler, label: "Perspective Matching" },
  { icon: Sun, label: "Lighting Adaptation" },
  { icon: Contrast, label: "Shadow Consistency" },
  { icon: Monitor, label: "High-Resolution Rendering" },
];

const PLATFORM_FLOW = [
  "Product Catalog",
  "AI Processing Engine",
  "Visualization Experience",
  "Customer Interaction",
  "Analytics Dashboard",
];

const ENTERPRISE_FEATURES = [
  { icon: Users, label: "Multi-user Workspaces" },
  { icon: UserCog, label: "Team Management" },
  { icon: ShieldCheck, label: "Role-Based Permissions" },
  { icon: ScrollText, label: "Audit Logs" },
  { icon: Lock, label: "Enterprise Security" },
  { icon: Code2, label: "API Access" },
  { icon: InfinityIcon, label: "Unlimited Product Catalogs*" },
  { icon: Cloud, label: "Scalable Cloud Infrastructure" },
];

const FAQS = [
  {
    q: "Can I upload my own products?",
    a: "Yes. Businesses manage their own product catalogs.",
  },
  {
    q: "Does NEIV work on mobile devices?",
    a: "Yes. Customers can use any modern browser on desktop, tablet, or mobile.",
  },
  {
    q: "Can I integrate NEIV into my website?",
    a: "Yes. APIs and embedding options are available for supported plans.",
  },
  {
    q: "Is my product data secure?",
    a: "Yes. NEIV uses secure cloud infrastructure with encrypted data storage and role-based access controls.",
  },
  {
    q: "Which industries can use NEIV?",
    a: "Any business selling products that customers place in physical spaces, including furniture, paint, tiles, kitchens, bathrooms, lighting, décor, and commercial interiors.",
  },
];

export default function ProductsPage() {
  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-neutral-50 pb-10 pt-28 md:pb-14 md:pt-32">
          {/* Right-side video panel */}
          <div className="pointer-events-none absolute right-0 top-0 hidden h-full w-[42%] lg:block" aria-hidden="true">
            <video
              src={platformPreviewVideo}
              autoPlay
              muted
              loop
              playsInline
              className="h-full w-full object-cover saturate-[1.15] contrast-[1.05]"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-50 via-neutral-50/25 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-neutral-50/20 via-transparent to-neutral-50/20" />
          </div>

          <div className="relative mx-auto max-w-6xl px-4 md:px-8">
            <div className="max-w-xl">
              <HeroIntro eyebrow="Products" heading="One Platform. Endless Visualization Possibilities.">
                <p className="mt-4 max-w-lg text-lg leading-relaxed text-neutral-500 md:text-xl">
                  NEIV brings together AI-powered visualization, product management,
                  customer engagement, and business analytics into a single
                  platform — helping businesses showcase products in real spaces
                  before customers buy.
                </p>
                <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                  <Link
                    to="/signup"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary/90 sm:w-auto"
                  >
                    <Rocket className="h-4 w-4" />
                    Start Free
                  </Link>
                  <a
                    href={CALENDLY_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 px-8 py-3.5 text-base font-semibold text-neutral-800 transition-all hover:border-neutral-900 sm:w-auto"
                  >
                    <CalendarDays className="h-4 w-4" />
                    Book a Demo
                  </a>
                </div>
              </HeroIntro>
            </div>
          </div>
        </section>

        {/* Supported categories */}
        <section className="relative overflow-hidden bg-neutral-50">
          {/* Header — sits above the marquee, no gap */}
          <div className="relative z-10 mx-auto max-w-4xl px-4 pt-14 pb-8 text-center md:px-8">
            <h2 className="text-3xl font-bold text-neutral-900 md:text-4xl">
              Built for every product that belongs in a space
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-neutral-500">
              Whether you're selling furniture, flooring, paint, lighting,
              décor, or modular kitchens, NEIV helps customers experience
              products in their own environment before making a purchase.
            </p>
          </div>

          {/* Marquee bands — directly below header, no gap */}
          <div className="relative overflow-hidden pb-10">
            <div className="space-y-4 [-webkit-mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)] [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
              <MarqueeRow images={MARQUEE_ROW_1} duration={34} />
              <MarqueeRow images={MARQUEE_ROW_2} duration={40} reverse />
            </div>
          </div>
        </section>

        {/* Core products */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Core Products</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Everything your business needs, in one place
              </h2>
            </div>

            <div className="mt-16 space-y-16">
              {CORE_PRODUCTS.map((product, i) => {
                const Icon = product.icon;
                const reversed = i % 2 === 1;
                return (
                  <div
                    key={product.name}
                    className={`flex flex-col items-center gap-10 md:gap-16 ${
                      reversed ? "md:flex-row-reverse" : "md:flex-row"
                    }`}
                  >
                    <div className="w-full md:w-1/2">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="mt-4 text-2xl font-bold text-neutral-900 md:text-3xl">
                        {product.name}
                      </h3>
                      <p className="mt-2 text-lg font-medium text-primary">{product.tagline}</p>
                      <p className="mt-3 max-w-md text-lg leading-relaxed text-neutral-500">
                        {product.body}
                      </p>
                    </div>
                    <div className="w-full md:w-1/2">
                      <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm shadow-neutral-900/[0.03]">
                        <img
                          src={product.img}
                          alt={product.name}
                          className="h-48 w-full object-cover"
                        />
                        <div className="p-8">
                          <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">
                            {product.listTitle}
                          </p>
                          <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                            {product.items.map((item) => (
                              <li key={item} className="flex items-start gap-2.5 text-neutral-700">
                                <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                                <span className="text-sm font-medium">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* AI capabilities */}
        <section className="relative overflow-hidden bg-neutral-50 py-24">
          {/* Soft ambient glow for depth */}
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute -top-20 right-[-8%] h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
            <div className="absolute -bottom-24 left-[-8%] h-72 w-72 rounded-full bg-neutral-200/70 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">AI Capabilities</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Powered by intelligent scene understanding
              </h2>
              <p className="mt-4 text-lg text-neutral-500">
                NEIV analyzes room structure to generate realistic
                visualizations while preserving perspective, lighting, and
                spatial consistency.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {AI_CAPABILITIES.map((cap) => {
                const Icon = cap.icon;
                return (
                  <div
                    key={cap.label}
                    className="group rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm shadow-neutral-900/[0.03] transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg hover:shadow-neutral-900/[0.06]"
                  >
                    <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform duration-300 group-hover:scale-110">
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="mt-3 text-sm font-semibold text-neutral-800">{cap.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Platform overview */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-3xl px-4 text-center md:px-8">
            <p className="text-sm font-bold uppercase tracking-widest text-primary">Platform Overview</p>
            <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
              One ecosystem connecting businesses, products, customers, and AI
            </h2>
            <div className="mt-12 flex flex-col items-center">
              {PLATFORM_FLOW.map((node, i) => (
                <React.Fragment key={node}>
                  <div className="w-full max-w-sm rounded-xl border border-neutral-200 bg-neutral-50 px-6 py-4 text-base font-semibold text-neutral-800 shadow-sm shadow-neutral-900/[0.03]">
                    {node}
                  </div>
                  {i < PLATFORM_FLOW.length - 1 && (
                    <ArrowDown className="my-2 h-5 w-5 text-primary" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* Enterprise features */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Enterprise Features</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Designed for businesses of every size
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {ENTERPRISE_FEATURES.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={feature.label}
                    className="flex items-center gap-4 rounded-2xl bg-white p-6 shadow-sm shadow-neutral-900/[0.03]"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="text-sm font-semibold text-neutral-800">{feature.label}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-center text-sm text-neutral-400">* Depending on plan.</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-3xl px-4 md:px-8">
            <div className="text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">FAQ</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Frequently asked questions
              </h2>
            </div>
            <Accordion type="single" collapsible className="mt-12">
              {FAQS.map((faq, i) => (
                <AccordionItem
                  key={faq.q}
                  value={`faq-${i}`}
                  className="mb-3 rounded-xl border border-neutral-200 bg-white px-6 shadow-sm shadow-neutral-900/[0.02]"
                >
                  <AccordionTrigger className="text-left text-base font-semibold text-neutral-900 hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-neutral-500">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <FinalCTA
          title="Ready to deliver smarter buying experiences?"
          subtitle="Give customers the confidence to make better purchase decisions with AI-powered visualization."
          primaryLabel="Start Free"
          secondaryLabel="Book a Demo"
        />
      </main>
      <Footer />
    </div>
  );
}
